import {
    find,
    reject,
    map,
    isNil,
    size,
    compose,
    filter,
    get,
    head,
    merge,
    overSome,
    negate,
    isEqual,
} from 'lodash/fp';
import { createSelector } from 'reselect';
import { getStringProp } from '@mews/fp';
import { MessageThreadType } from '@mews-commander/enums';
import { Thread } from '@mews-commander/legacy-data-structures';
import { utcToLocalizedDateTime, getOrNull, getOrFalse } from '@mews-commander/utils';
import { getCurrentProfileId, getCurrentUser } from '@mews-commander/core';
import { getCurrentEnterpriseThreads } from '@mews-commander/legacy-entities/src/ducks/threads';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getCurrentScopeProfileId } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { sortByUpdatedUtcDescending } from '../../entityManagement/utils';
import { moduleSelector } from '../module';

export const getThreadDrafts = createSelector(moduleSelector, get('threadDrafts'));

export const getCurrentThreadId = createSelector(moduleSelector, get('currentThreadId'));

export const getAllThreads = createSelector(getCurrentEnterpriseThreads, getThreadDrafts, merge);

const getThreadById = (threads: $TSFixMe, id: $TSFixMe) => threads[id];
const currentThreadData = createSelector(getAllThreads, getCurrentThreadId, getThreadById);

export const getThreadDataByThreadId = createSelector(getStringProp('id'), getAllThreads, get);

export const isThreadPosted = compose(
    negate(overSome([getOrFalse('isDraft'), getOrFalse('isStub')])),
    getThreadDataByThreadId
);

export const getCurrentThreadType = createSelector(currentThreadData, get('type'));

const getCurrentThreadCreatorProfileId = createSelector(currentThreadData, get('creatorProfileId'));

export const canChangeCurrentThreadName = createSelector(
    getCurrentThreadCreatorProfileId,
    getCurrentScopeProfileId,
    isEqual
);

export const getCurrentViewMode = createSelector(moduleSelector, get('viewMode'));

const filterReadableThreads = (viewMode: $TSFixMe, threads: $TSFixMe) =>
    compose(reject('isDraft'), filter({ type: viewMode }))(threads);

const getVisibleThreads = createSelector(getCurrentViewMode, getAllThreads, filterReadableThreads);

export const threadList = createSelector(
    getVisibleThreads,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (threads, timezone) =>
        compose(
            map((item) =>
                Thread({
                    ...item,
                    updated: utcToLocalizedDateTime((item as $TSFixMe).updatedUtc, timezone),
                })
            ),
            sortByUpdatedUtcDescending
        )(threads)
);

export const getLatestVisibleThread = createSelector(getVisibleThreads, head);

// @ts-expect-error $TSFixMeError: No overload matches this call.
export const getLatestVisibleThreadId = compose(getOrNull('id'), getLatestVisibleThread);

export const currentThread = createSelector(
    currentThreadData,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (thread, timezone) => {
        if (isNil(thread)) {
            return null;
        }
        return Thread({
            ...thread,
            updated: utcToLocalizedDateTime(thread.updatedUtc, timezone),
        });
    }
);

export const threadsFetchingOffset = createSelector(getVisibleThreads, size);

export const areThreadsLoading = createSelector(moduleSelector, get('threadsLoading'));
const areAllThreadsFetched = createSelector(moduleSelector, get('allThreadsLoaded'));

const areAllCurrentViewModeThreadsFetched = createSelector(getCurrentViewMode, areAllThreadsFetched, get);

export const canLoadMoreThreads = createSelector(
    areAllCurrentViewModeThreadsFetched,
    areThreadsLoading,
    (allLoaded, loading) => !allLoaded && !loading
);

export const currentThreadSubject = createSelector([getAllThreads, getCurrentThreadId], (threads, threadId) => {
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    const thread = find({ id: threadId }, threads);
    return isNil(thread) ? '' : (thread as $TSFixMe).subject;
});

export const isCurrentThreadStub = createSelector(currentThreadData, (thread) => thread && thread.isStub);

export const isCurrentThreadDraft = createSelector(currentThreadData, (thread) => thread && thread.isDraft);

const filterUnreadThreadsByType = (messageThreadType: $TSFixMe) => filter({ type: messageThreadType, isRead: false });

const getUnreadEnterpriseThreads = createSelector(
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    getAllThreads,
    filterUnreadThreadsByType(MessageThreadType.Enterprise)
);

// @ts-expect-error $TSFixMeError: No overload matches this call.
const getUnreadPrivateThreads = createSelector(getAllThreads, filterUnreadThreadsByType(MessageThreadType.Private));

export const getUnreadEnterpriseThreadsCount = createSelector(getUnreadEnterpriseThreads, size);

export const getUnreadPrivateThreadsCount = createSelector(getUnreadPrivateThreads, size);

export const isCurrentThreadRead = compose(get('isRead'), currentThreadData);

export const getCurrentUserData = createSelector(
    () => getCurrentProfileId(),
    () => getCurrentUser(),
    (profileId, { id, email, firstName, lastName, imageId }) => ({ id, profileId, email, firstName, lastName, imageId })
);
